import api from '../api';
import { getAuthHeaders } from '../hooks/useAuthHeaders';

/**
 * @typedef {Object} Question
 * @property {string} _id
 * @property {string} text
 * @property {string} settledBy  What would close the loop, if anything named yet
 * @property {object} [inquiry]  Requested Library look bound to this question
 * @property {string} status
 * @property {string} linkedTagName
 * @property {string} [createdAt]
 * @property {string} [updatedAt]
 */

export const getQuestions = async ({ status, tag } = {}) => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (tag) params.set('tag', tag);
  const query = params.toString();
  const res = await api.get(`/api/questions${query ? `?${query}` : ''}`, getAuthHeaders());
  return res.data || [];
};

export const getQuestion = async (id) => {
  const res = await api.get(`/api/questions/${id}`, getAuthHeaders());
  return res.data;
};

export const getConceptQuestions = async (conceptName, { status = 'open' } = {}) => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  const query = params.toString();
  const res = await api.get(
    `/api/concepts/${encodeURIComponent(conceptName)}/questions${query ? `?${query}` : ''}`,
    getAuthHeaders()
  );
  return res.data || [];
};

export const createQuestion = async ({
  text,
  settledBy = '',
  conceptName,
  status = 'open',
  blocks = [],
  linkedHighlightIds = []
} = {}) => {
  const payload = {
    text,
    settledBy,
    status,
    conceptName: conceptName || '',
    linkedTagName: conceptName || '',
    blocks,
    linkedHighlightIds: Array.isArray(linkedHighlightIds) ? linkedHighlightIds : []
  };
  const res = await api.post('/api/questions', payload, getAuthHeaders());
  return res.data;
};

export const updateQuestion = async (id, payload) => {
  const res = await api.put(`/api/questions/${id}`, payload, getAuthHeaders());
  return res.data;
};

export const getQuestionShare = async (id) => {
  const res = await api.get(`/api/questions/${encodeURIComponent(id)}/share`, getAuthHeaders());
  return res.data || { shared: false };
};

export const mintQuestionShare = async (id, body = {}) => {
  const res = await api.post(`/api/questions/${encodeURIComponent(id)}/share`, body, getAuthHeaders());
  return res.data || {};
};

export const updateQuestionShare = async (id, body = {}) => {
  const res = await api.put(`/api/questions/${encodeURIComponent(id)}/share`, body, getAuthHeaders());
  return res.data || {};
};

export const revokeQuestionShare = async (id) => {
  const res = await api.delete(`/api/questions/${encodeURIComponent(id)}/share`, getAuthHeaders());
  return res.data || { revoked: true };
};

const optionalAuthHeaders = () => (
  typeof localStorage !== 'undefined' && localStorage.getItem('token')
    ? getAuthHeaders()
    : undefined
);

export const getPublicQuestion = async (slug) => {
  const res = await api.get(
    `/api/public/questions/${encodeURIComponent(slug)}`,
    optionalAuthHeaders()
  );
  return res.data;
};

export const offerQuestionContribution = async (slug, body = {}) => {
  const res = await api.post(
    `/api/public/questions/${encodeURIComponent(slug)}/contributions`,
    body,
    optionalAuthHeaders()
  );
  return res.data || { sent: true };
};

export const withdrawQuestionContribution = async (slug, contributionId) => {
  const res = await api.delete(
    `/api/public/questions/${encodeURIComponent(slug)}/contributions/${encodeURIComponent(contributionId)}`,
    optionalAuthHeaders()
  );
  return res.data || { withdrawn: true };
};

export const getQuestionPresence = async (slug) => {
  const res = await api.get(
    `/api/public/questions/${encodeURIComponent(slug)}/presence`,
    optionalAuthHeaders()
  );
  return res.data || {};
};

export const beatQuestionPresence = async (slug) => {
  const res = await api.put(
    `/api/public/questions/${encodeURIComponent(slug)}/presence`,
    {},
    optionalAuthHeaders()
  );
  return res.data || {};
};

export const interpretQuestionContribution = async (questionId, contributionId, body = {}) => {
  const res = await api.patch(
    `/api/questions/${encodeURIComponent(questionId)}/share/contributions/${encodeURIComponent(contributionId)}`,
    body,
    getAuthHeaders()
  );
  return res.data || {};
};

export const saveQuestionShareBrief = async (questionId, body = {}) => {
  const res = await api.patch(
    `/api/questions/${encodeURIComponent(questionId)}/share/brief`,
    body,
    getAuthHeaders()
  );
  return res.data || {};
};

export const saveQuestionShareSuccession = async (questionId, body = {}) => {
  const res = await api.patch(
    `/api/questions/${encodeURIComponent(questionId)}/share/succession`,
    body,
    getAuthHeaders()
  );
  return res.data || {};
};

export const saveQuestionShareMandate = async (questionId, body = {}) => {
  const res = await api.patch(
    `/api/questions/${encodeURIComponent(questionId)}/share/mandate`,
    body,
    getAuthHeaders()
  );
  return res.data || {};
};

export const importQuestionShareRecords = async (questionId, body = {}) => {
  const res = await api.post(
    `/api/questions/${encodeURIComponent(questionId)}/share/records`,
    body,
    getAuthHeaders()
  );
  return res.data || {};
};

export const placeQuestionContribution = async (questionId, contributionId) => {
  const res = await api.post(
    `/api/questions/${encodeURIComponent(questionId)}/share/contributions/${encodeURIComponent(contributionId)}/place`,
    {},
    getAuthHeaders()
  );
  return res.data || {};
};
